import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, startOfMonth, endOfMonth, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { api } from '../api/client';
import { ETAPA_MAP, ETAPAS, PRIORIDADES, CANAIS } from '../lib/constants';
import { parseDate } from '../lib/dates';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = { 'pt-BR': ptBR };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 0 }), getDay, locales });

const messages = {
  allDay: 'Dia inteiro',
  previous: 'Anterior',
  next: 'Próximo',
  today: 'Hoje',
  month: 'Mês',
  week: 'Semana',
  day: 'Dia',
  agenda: 'Agenda',
  date: 'Data',
  time: 'Hora',
  event: 'Evento',
  noEventsInRange: 'Sem demandas no período.',
  showMore: (total) => `+${total} mais`,
};

const ETAPA_COLORS = {
  nova_demanda: '#0369a1',
  aprovada: '#1e40af',
  em_captacao: '#7c3aed',
  em_edicao: '#d97706',
  aguardando_aprovacao: '#d97706',
  pronta_para_publicar: '#0891b2',
  publicado: '#16a34a',
  cancelada: '#64748b',
};

export function CalendarioPage() {
  const nav = useNavigate();
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [secretarias, setSecretarias] = useState([]);
  const [filtros, setFiltros] = useState({
    etapa: '',
    secretaria_id: '',
    prioridade: '',
    canal: '',
    campo: 'acao', // acao | postagem | ambos
  });
  const [incluirCanceladas, setIncluirCanceladas] = useState(
    () => localStorage.getItem('calendario.incluirCanceladas') === 'true'
  );
  useEffect(() => { localStorage.setItem('calendario.incluirCanceladas', String(incluirCanceladas)); }, [incluirCanceladas]);

  const range = useMemo(() => {
    const de = addDays(startOfMonth(date), -10);
    const ate = addDays(endOfMonth(date), 10);
    return { de: format(de, 'yyyy-MM-dd'), ate: format(ate, 'yyyy-MM-dd') };
  }, [date]);

  useEffect(() => { api.get('/secretarias').then((r) => setSecretarias(r.secretarias)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ de: range.de, ate: range.ate });
    if (incluirCanceladas) params.set('incluir_canceladas', 'true');
    api.get(`/demandas/calendario?${params.toString()}`)
      .then((r) => setRaw(r.eventos))
      .finally(() => setLoading(false));
  }, [range.de, range.ate, incluirCanceladas]);

  const eventos = useMemo(() => {
    const out = [];
    for (const d of raw) {
      if (filtros.etapa && d.etapa !== filtros.etapa) continue;
      if (filtros.secretaria_id && String(d.secretaria_id) !== String(filtros.secretaria_id)) continue;
      if (filtros.prioridade && d.prioridade !== filtros.prioridade) continue;
      if (filtros.canal && !(d.canais || []).includes(filtros.canal)) continue;

      const useAcao = filtros.campo !== 'postagem' && d.data_acao;
      const usePost = filtros.campo !== 'acao' && d.data_postagem;

      if (useAcao) {
        const dtInicio = parseDate(d.data_acao);
        // react-big-calendar precisa de `end` exclusivo. Pra ação multi-dia
        // (data_acao_fim preenchido), end = dia seguinte ao fim. Pra 1 dia só,
        // end = início + 1 dia também (mesma lógica, sem efeito visual).
        const dtFimReal = d.data_acao_fim ? parseDate(d.data_acao_fim) : dtInicio;
        const dtEnd = new Date(dtFimReal);
        dtEnd.setDate(dtEnd.getDate() + 1);
        out.push({
          id: `${d.id}-a`,
          demandaId: d.codigo,
          title: d.data_acao_fim ? `${d.titulo} 📅` : d.titulo,
          start: dtInicio,
          end: dtEnd,
          allDay: true,
          resource: { ...d, _campo: 'acao' },
        });
      }
      if (usePost) {
        const dt = parseDate(d.data_postagem);
        out.push({
          id: `${d.id}-p`,
          demandaId: d.codigo,
          title: d.titulo,
          start: dt,
          end: dt,
          allDay: true,
          resource: { ...d, _campo: 'postagem' },
        });
      }
    }
    return out;
  }, [raw, filtros]);

  const eventStyleGetter = (event) => {
    const color = ETAPA_COLORS[event.resource.etapa] || '#64748b';
    const isPost = event.resource._campo === 'postagem';
    return {
      style: {
        background: color,
        color: 'white',
        border: isPost ? '2px dashed rgba(255,255,255,0.6)' : 'none',
        opacity: event.resource.etapa === 'cancelada' ? 0.55 : 1,
      },
    };
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Calendário</h1>
          <p className="page-subtitle">{eventos.length} eventos no período</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-body">
          <div className="grid-3" style={{ gap: 12 }}>
            <div className="field">
              <label>Campo de data</label>
              <select className="select" value={filtros.campo} onChange={(e) => setFiltros((f) => ({ ...f, campo: e.target.value }))}>
                <option value="ambos">Ação + postagem</option>
                <option value="acao">Data da ação</option>
                <option value="postagem">Data da postagem</option>
              </select>
            </div>
            <div className="field">
              <label>Etapa</label>
              <select className="select" value={filtros.etapa} onChange={(e) => setFiltros((f) => ({ ...f, etapa: e.target.value }))}>
                <option value="">Todas</option>
                {ETAPAS.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Secretaria</label>
              <select className="select" value={filtros.secretaria_id} onChange={(e) => setFiltros((f) => ({ ...f, secretaria_id: e.target.value }))}>
                <option value="">Todas</option>
                {secretarias.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Prioridade</label>
              <select className="select" value={filtros.prioridade} onChange={(e) => setFiltros((f) => ({ ...f, prioridade: e.target.value }))}>
                <option value="">Todas</option>
                {PRIORIDADES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Canal</label>
              <select className="select" value={filtros.canal} onChange={(e) => setFiltros((f) => ({ ...f, canal: e.target.value }))}>
                <option value="">Todos</option>
                {CANAIS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div className="row" style={{ marginTop: 10 }}>
            <label className="row" style={{ gap: 6, fontSize: 13, color: 'var(--c-text-muted)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={incluirCanceladas}
                onChange={(e) => setIncluirCanceladas(e.target.checked)}
              />
              Mostrar canceladas (com opacidade reduzida)
            </label>
          </div>

          <div className="row-wrap" style={{ marginTop: 12, gap: 12, fontSize: 12 }}>
            <strong className="text-xs muted" style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Legenda:</strong>
            {ETAPAS.map((e) => (
              <span key={e.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: ETAPA_COLORS[e.key] }} />
                {e.label}
              </span>
            ))}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 12 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: '#64748b', border: '2px dashed rgba(255,255,255,0.6)' }} />
              data de postagem
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loader-full"><span className="spinner" /></div>
      ) : (
        <div style={{ height: 'calc(100vh - 320px)', minHeight: 500 }}>
          <Calendar
            localizer={localizer}
            culture="pt-BR"
            messages={messages}
            events={eventos}
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            views={['month', 'week', 'day', 'agenda']}
            popup
            eventPropGetter={eventStyleGetter}
            onSelectEvent={(e) => nav(`/demandas/${e.demandaId}`)}
            startAccessor="start"
            endAccessor="end"
          />
        </div>
      )}
    </div>
  );
}
